<template>
  <div ref="root" class="relative">
    <!-- Trigger -->
    <button
      ref="trigger"
      type="button"
      class="flex items-center rounded-full border-2 border-highlight overflow-hidden hover:border-orange-400"
      :class="sizeClass"
      aria-haspopup="menu"
      :aria-expanded="open ? 'true' : 'false'"
      :aria-label="$t('account.menuFor', { name: userName })"
      @click="toggle"
      @keydown.down.prevent="openMenu(true)"
    >
      <img :src="avatar" alt="" class="h-full w-full object-cover" />
    </button>

    <!-- Menu -->
    <div
      v-if="open"
      ref="menu"
      role="menu"
      class="absolute right-0 mt-2 w-56 z-40 rounded-xl border border-primary-400 bg-primary-500 py-2 shadow-lg"
      @keydown.esc="closeMenu(true)"
      @keydown.tab="trapFocus"
    >
      <!-- Who am I -->
      <div class="px-4 py-2 border-b border-primary-400">
        <p class="text-white text-sm font-semibold truncate">{{ userName }}</p>
        <p class="text-subtle text-xs truncate">{{ email }}</p>
        <span
          class="mt-2 inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide"
          :class="
            isPro
              ? 'bg-highlight text-primary-700'
              : 'bg-primary-700 text-subtle'
          "
        >
          {{ $t(isPro ? "account.planPro" : "account.planFree") }}
        </span>
      </div>

      <router-link
        v-for="item in items"
        :key="item.to"
        :to="item.to"
        role="menuitem"
        class="flex items-center gap-3 px-4 py-3 text-sm text-muted hover:text-highlight hover:bg-primary-700"
        @click.native="closeMenu(false)"
      >
        <Font-awesome-icon
          :icon="['fas', item.icon]"
          class="w-4 flex-none"
          aria-hidden="true"
        />
        {{ $t(item.label) }}
      </router-link>

      <div class="my-1 border-t border-primary-400"></div>

      <button
        ref="lastItem"
        type="button"
        role="menuitem"
        class="flex w-full items-center gap-3 px-4 py-3 text-sm text-muted hover:text-highlight hover:bg-primary-700"
        :disabled="signingOut"
        @click="signOut"
      >
        <LoadingSymbol v-if="signingOut" size="1x" class="w-4 flex-none" />
        <Font-awesome-icon
          v-else
          :icon="['fas', 'arrow-right-from-bracket']"
          class="w-4 flex-none"
          aria-hidden="true"
        />
        {{ $t("account.signOut") }}
      </button>
    </div>
  </div>
</template>

<script>
import LoadingSymbol from "@/components/common/LoadingSymbol.vue";

export default {
  name: "UserMenu",
  components: { LoadingSymbol },

  props: {
    // Desktop uses a larger avatar than the mobile top bar.
    size: { type: String, default: "md" },
  },

  data() {
    return {
      open: false,
      signingOut: false,
      items: [
        { to: "/profile", label: "account.profile", icon: "user" },
        { to: "/settings", label: "account.settings", icon: "gear" },
      ],
    };
  },

  computed: {
    sizeClass() {
      return this.size === "sm" ? "h-9 w-9" : "h-12 w-12";
    },
    userName() {
      return this.$store.getters.authUser?.name ?? "Profile";
    },
    email() {
      return this.$store.getters.authUser?.email ?? "";
    },
    isPro() {
      return this.$store.getters.isProUser;
    },
    avatar() {
      return process.env.VUE_APP_IMAGE || "/reeltrack-default.png";
    },
  },

  watch: {
    // Any navigation closes the menu (e.g. the browser back button).
    $route() {
      this.open = false;
    },
  },

  mounted() {
    document.addEventListener("click", this.onDocumentClick);
  },

  beforeDestroy() {
    document.removeEventListener("click", this.onDocumentClick);
  },

  methods: {
    toggle() {
      this.open ? this.closeMenu(false) : this.openMenu(false);
    },

    openMenu(focusFirst) {
      this.open = true;
      if (!focusFirst) return;
      this.$nextTick(() => {
        this.$refs.menu?.querySelector('[role="menuitem"]')?.focus();
      });
    },

    /** `returnFocus` puts the caret back on the avatar, as Escape should. */
    closeMenu(returnFocus) {
      this.open = false;
      if (returnFocus) {
        this.$nextTick(() => this.$refs.trigger?.focus());
      }
    },

    onDocumentClick(event) {
      if (this.open && !this.$refs.root?.contains(event.target)) {
        this.open = false;
      }
    },

    /** Keep Tab inside the open menu. */
    trapFocus(event) {
      const focusable = this.$refs.menu?.querySelectorAll(
        '[role="menuitem"]:not([disabled])',
      );
      if (!focusable || !focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    },

    signOut() {
      if (this.signingOut) return;
      this.signingOut = true;
      // The store clears the session and routes to login even if the API call
      // fails, so there is no failure path to surface here.
      this.$store.dispatch("logout").finally(() => {
        this.signingOut = false;
        this.open = false;
      });
    },
  },
};
</script>
